import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ChevronDown,
  ExternalLink,
  Home,
  LayoutGrid,
  MapPin,
  Menu,
  Monitor,
  Package,
  Search,
  ShoppingBag,
  Tag,
  Truck,
  User,
  X,
} from "lucide-react";
import {
  DEMO_BRANDS,
  SHOP_CATEGORY_CARDS,
  SHOP_HERO_SLIDES,
  SHOP_PRODUCTS,
  SHOP_QUICK_LINKS,
  SHOP_TRUST_HIGHLIGHTS,
  SHOP_VOUCHERS,
} from "../../data/shopDemoMock";
import { cn } from "../../lib/utils";
import styles from "./ShopDemo.module.css";

const FONT_LINK_ID = "littlenest-demo-fonts";
const logoSrc = `${process.env.PUBLIC_URL || ""}/brand/littlenest-logo.svg`;

function babyShopUrl() {
  const fromEnv = process.env.REACT_APP_BABY_SHOP_URL?.trim();
  if (fromEnv) return fromEnv.replace(/\/$/, "");
  return "http://localhost:3003";
}

const SHOP_CSS_VARS = {
  "--paper": "#fffaf4",
  "--ink": "#1f2d3d",
  "--muted": "#627181",
  "--accent": "#ef7f4d",
  "--accent-hover": "#df6d39",
  "--secondary": "#2f5d8a",
  "--line": "#f1dcc3",
  "--line-strong": "#d7b892",
  "--glow": "rgba(239, 127, 77, 0.22)",
  "--card": "#ffffff",
  "--shadow-soft": "0 14px 36px rgba(33, 44, 63, 0.09)",
  "--shadow-float": "0 24px 70px rgba(33, 44, 63, 0.12)",
  "--font-promo": '"Nunito Sans", "Segoe UI", sans-serif',
};

function formatIdr(n) {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(n);
}

function formatVoucherValue(v) {
  if (v.type === "PERCENT") return `${v.value}%`;
  if (v.value >= 1000) return `${Math.round(v.value / 1000)}RB`;
  return String(v.value);
}

function renderStars(rating) {
  const full = Math.round(rating || 0);
  return Array.from({ length: 5 }).map((_, i) => (
    <span key={i} className={i < full ? "text-amber-500" : "text-amber-200"}>
      {"\u2605"}
    </span>
  ));
}

function effectivePrice(p) {
  const sp = p.salePrice;
  if (sp != null && sp < p.price) return sp;
  return p.price;
}

function saleActive(p) {
  return p.salePrice != null && p.salePrice < p.price;
}

function PromoBarStrip() {
  return (
    <div className={styles.promoBar} role="region" aria-label="Promo bar">
      <button type="button" className={styles.promoLink}>
        <div className={styles.promoContent}>
          <span className={styles.promoLead}>Belanja kebutuhan bayi</span>
          <span className={styles.promoDiscountWrap}>
            <span className={styles.promoDiscountLabel}>
              <span>Diskon</span>
              <span>hingga</span>
            </span>
            <span className={styles.promoDiscountValue}>30%</span>
          </span>
          <span className={styles.promoDot} aria-hidden />
          <span className={styles.promoText}>Gratis ongkir area terpilih</span>
          <span className={styles.promoDot} aria-hidden />
          <span className={styles.promoText}>Kualitas premium untuk si kecil</span>
        </div>
      </button>
    </div>
  );
}

function HeroCarousel({ slides }) {
  const [idx, setIdx] = useState(0);
  const current = slides[idx] ?? slides[0];

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 5600);
    return () => clearInterval(t);
  }, [slides.length]);

  if (!current) return null;

  return (
    <section className="relative h-[280px] overflow-hidden rounded-[34px] border border-[var(--line)] bg-[var(--card)] shadow-[var(--shadow-float)] sm:h-[360px] md:h-[520px]">
      <img
        key={current.id}
        src={current.imageUrl}
        alt=""
        className="absolute inset-0 h-full w-full object-cover transition-opacity duration-500"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/16 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/36 via-black/10 to-transparent" />

      <div className="relative flex h-full items-end p-5 sm:p-7 md:p-10">
        <div className="max-w-[620px] text-white">
          {current.badge ? (
            <div className="inline-flex rounded-full border border-white/35 bg-white/18 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em]">
              {current.badge}
            </div>
          ) : null}
          <h2 className="mt-3 font-['Fraunces',Georgia,serif] text-2xl font-semibold leading-tight text-white sm:text-3xl md:text-4xl">
            {current.title}
          </h2>
          {current.subtitle ? (
            <p className="mt-2 max-w-[520px] text-sm text-white/90 sm:text-base">{current.subtitle}</p>
          ) : null}
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#2f5d8a]">
              Belanja Sekarang
            </span>
            <span className="inline-flex items-center rounded-full border border-white/44 bg-white/14 px-5 py-2.5 text-sm font-semibold text-white">
              Lihat Koleksi
            </span>
          </div>
        </div>
      </div>

      {slides.length > 1 ? (
        <div className="absolute bottom-4 right-5 flex items-center gap-2 sm:bottom-5 sm:right-7">
          {slides.map((s, dotIdx) => (
            <button
              key={s.id}
              type="button"
              aria-label={`Slide ${dotIdx + 1}`}
              onClick={() => setIdx(dotIdx)}
              className={cn(
                "h-2 rounded-full transition-all",
                dotIdx === idx ? "w-6 bg-white" : "w-2 bg-white/45 hover:bg-white/70"
              )}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function StoreFooter() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState(null);

  const linksCustomerCare = [
    { label: "Contact Us", href: "#" },
    { label: "How to Order", href: "#" },
    { label: "FAQ", href: "#" },
    { label: "Shipping & Return", href: "#" },
    { label: "Terms & Conditions", href: "#" },
  ];
  const linksAbout = [
    { label: "About LittleNest", href: "#" },
    { label: "Partnership", href: "#" },
    { label: "Reseller", href: "#" },
    { label: "Join Our Team", href: "#" },
    { label: "Store Locator", href: "#" },
  ];
  const linksAccount = [
    { label: "Sign In", href: "#" },
    { label: "Register", href: "#" },
    { label: "Track Order", href: "#" },
    { label: "My Cart", href: "#" },
    { label: "Shop", href: "#" },
  ];

  return (
    <footer className="mt-14 border-t border-[var(--line)] bg-gradient-to-b from-[#fff9f0] via-[#fffdfa] to-[#fff6eb]">
      <div className="mx-auto w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        <div className="py-8 md:py-10">
          <div className="rounded-[28px] border border-[var(--line)] bg-white/90 p-5 shadow-[var(--shadow-soft)] md:p-7">
            <div className="grid gap-6 lg:grid-cols-[1.35fr_1fr_1fr_1fr]">
              <div>
                <img src={logoSrc} alt="LittleNest Baby Shop" className="h-14 w-auto md:h-16" />
                <p className="mt-3 max-w-md text-sm leading-relaxed text-[color:var(--muted)]">
                  Toko kebutuhan bayi dan ibu dengan produk pilihan yang aman, nyaman, dan terkurasi.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {["Instagram", "YouTube", "WhatsApp", "TikTok"].map((label) => (
                    <span
                      key={label}
                      className="inline-flex items-center rounded-full border border-[var(--line)] bg-white px-3 py-1.5 text-xs font-semibold text-[color:var(--ink)]"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </div>
              {[
                { title: "Customer Care", items: linksCustomerCare },
                { title: "About", items: linksAbout },
                { title: "Account", items: linksAccount },
              ].map((col) => (
                <div key={col.title}>
                  <div className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[color:var(--muted)]">{col.title}</div>
                  <ul className="mt-3 space-y-2 text-sm">
                    {col.items.map((item) => (
                      <li key={item.label}>
                        <span className="cursor-default text-[color:var(--ink)]/85">{item.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="my-6 h-px bg-[rgba(31,45,61,0.11)]" />

            <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
              <div>
                <div className="text-base font-semibold text-[color:var(--ink)]">Dapatkan update promo dan produk baru</div>
                <div className="mt-1 text-sm text-[color:var(--muted)]">Newsletter simulasi — pratinjau portofolio.</div>
                <form
                  className="mt-3 flex w-full max-w-xl items-center gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!email.trim()) {
                      setMsg("Email masih kosong.");
                      return;
                    }
                    setMsg("Terima kasih (simulasi).");
                    setEmail("");
                  }}
                >
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Masukkan email"
                    className="h-11 w-full rounded-full border border-[var(--line)] bg-white px-4 text-sm outline-none transition focus:border-[var(--accent)]"
                  />
                  <button
                    type="submit"
                    className="h-11 shrink-0 rounded-full bg-[var(--accent)] px-5 text-sm font-semibold text-white transition hover:bg-[var(--accent-hover)]"
                  >
                    Subscribe
                  </button>
                </form>
                {msg ? <div className="mt-2 text-xs text-[color:var(--muted)]">{msg}</div> : null}
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs text-[color:var(--muted)] sm:grid-cols-4">
                {["BCA", "Mandiri", "Visa", "Mastercard", "GoPay", "OVO", "JNE", "GoSend"].map((m) => (
                  <div
                    key={m}
                    className="rounded-full border border-[var(--line)] bg-white px-3 py-1.5 text-center font-semibold text-[color:var(--ink)]/80"
                  >
                    {m}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-4 pb-6 text-center text-xs text-[color:var(--muted)]">
            Copyright (c) {new Date().getFullYear()} LittleNest Baby Shop — demo portofolio.
          </div>
        </div>
      </div>
    </footer>
  );
}

function MobileBottomNavDemo() {
  const items = [
    { label: "Home", Icon: Home, active: true },
    { label: "Shop", Icon: Package, active: false },
    { label: "Promo", Icon: Tag, active: false },
    { label: "Cart", Icon: ShoppingBag, active: false, badge: 2 },
    { label: "Akun", Icon: User, active: false },
  ];

  return (
    <div className="fixed inset-x-0 bottom-3 z-[60] px-3 lg:hidden">
      <div className="mx-auto flex w-full max-w-xl items-center justify-between rounded-[24px] border border-[var(--line)] bg-white/92 px-2 py-2 shadow-[0_16px_40px_rgba(33,44,63,0.2)] backdrop-blur">
        {items.map(({ label, Icon, active, badge }) => (
          <button
            key={label}
            type="button"
            className={cn(
              "relative flex min-w-[62px] flex-col items-center gap-1 rounded-2xl px-2 py-1.5 text-[11px] font-semibold transition",
              active ? "bg-[rgba(239,127,77,0.14)] text-[color:var(--ink)]" : "text-[color:var(--muted)]"
            )}
          >
            <span
              className={cn(
                "inline-flex h-7 w-7 items-center justify-center rounded-full border",
                active
                  ? "border-[var(--accent)] bg-[var(--accent)] text-white"
                  : "border-[var(--line)] bg-white text-[color:var(--muted)]"
              )}
            >
              <Icon className="h-4 w-4" />
            </span>
            <span>{label}</span>
            {badge ? (
              <span className="absolute right-1 top-0 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] text-white">
                {badge}
              </span>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ShopDemo() {
  const liveBase = babyShopUrl();
  const [view, setView] = useState("preview");
  const [cat, setCat] = useState("all");
  const [pickupOpen, setPickupOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const katalogRef = React.useRef(null);

  useEffect(() => {
    if (!document.getElementById(FONT_LINK_ID)) {
      const link = document.createElement("link");
      link.id = FONT_LINK_ID;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,600;0,9..144,700;1,9..144,400&family=Nunito+Sans:ital,opsz,wght@0,6..12,700;0,6..12,800;0,6..12,900;1,6..12,700&family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  const filtered = useMemo(() => {
    if (cat === "all") return SHOP_PRODUCTS;
    return SHOP_PRODUCTS.filter((p) => p.category === cat);
  }, [cat]);

  const best = SHOP_PRODUCTS.slice(0, 3);

  function scrollToKatalog() {
    katalogRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function selectCategory(filter) {
    setCat(filter);
    setMobileOpen(false);
    setTimeout(scrollToKatalog, 80);
  }

  const activeLabel =
    cat === "all" ? null : SHOP_CATEGORY_CARDS.find((c) => c.filter === cat)?.label ?? cat;

  const pickupLocations = [
    { name: "LittleNest Baby Shop HQ", address: "Jl. Jend. Sudirman No. 123, Jakarta" },
    { name: "LittleNest Baby Shop - Kemang", address: "Jl. Kemang Raya No. 45, Jakarta" },
  ];

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-slate-950">
      <header className="z-[70] flex shrink-0 flex-wrap items-center gap-2 border-b border-slate-800 bg-slate-950 px-3 py-2.5 sm:gap-3 sm:px-4">
        <Link
          to="/#projects"
          className="inline-flex items-center gap-2 text-sm text-slate-300 transition-colors hover:text-white"
        >
          <ArrowLeft className="h-4 w-4 shrink-0" />
          <span className="hidden sm:inline">Kembali ke portofolio</span>
          <span className="sm:hidden">Kembali</span>
        </Link>
        <span className="hidden h-4 w-px bg-slate-700 sm:block" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Portofolio</p>
          <p className="truncate text-sm font-semibold text-white">LittleNest — mirror UI (Next.js penuh: Embed)</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => setView("preview")}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium sm:px-3",
              view === "preview"
                ? "border-[#ef7f4d]/50 bg-[#3d2818] text-[#ffd4bc]"
                : "border-slate-700 text-slate-300 hover:bg-slate-800"
            )}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            Pratinjau
          </button>
          <button
            type="button"
            onClick={() => setView("embed")}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium sm:px-3",
              view === "embed"
                ? "border-[#ef7f4d]/50 bg-[#3d2818] text-[#ffd4bc]"
                : "border-slate-700 text-slate-300 hover:bg-slate-800"
            )}
          >
            <Monitor className="h-3.5 w-3.5" />
            Embed
          </button>
          <a
            href={`${liveBase}/`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-2.5 py-1.5 text-xs font-medium text-slate-200 hover:bg-slate-800 sm:px-3"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Tab baru
          </a>
        </div>
      </header>

      {view === "preview" ? (
        <div
          className="relative min-h-0 flex-1 overflow-y-auto pb-24 lg:pb-10"
          style={{
            ...SHOP_CSS_VARS,
            fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
            color: "var(--ink)",
            backgroundColor: "var(--paper)",
            backgroundImage:
              "radial-gradient(circle at 8% -10%, rgba(239, 127, 77, 0.2) 0, transparent 42%), radial-gradient(circle at 92% 0%, rgba(71, 183, 165, 0.18) 0, transparent 36%), linear-gradient(180deg, #fffaf4 0%, #fffdf9 24%, #fff8f1 100%)",
          }}
        >
          {/* Store header — struktur headerClient + PromoBar */}
          <header className="sticky top-0 z-40 w-full border-b border-[var(--line)] bg-[var(--card)] backdrop-blur">
            <PromoBarStrip />

            <div className="mx-auto w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
              <div className="flex h-16 items-center gap-3 sm:h-[72px]">
                <button
                  type="button"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--line)] bg-[var(--card)] lg:hidden"
                  aria-label="Open menu"
                  onClick={() => setMobileOpen(true)}
                >
                  <Menu className="h-5 w-5 text-[var(--accent)]" />
                </button>

                <span className="flex items-center gap-2">
                  <img src={logoSrc} alt="LittleNest Baby Shop" className="h-9 w-auto sm:h-10 md:h-12 lg:h-14" />
                </span>

                <div className="hidden flex-1 lg:flex">
                  <div className="relative w-full max-w-[360px] xl:max-w-[420px]">
                    <div className="flex h-10 items-center gap-2 rounded-full border border-[var(--line)] bg-[var(--card)] px-3 shadow-sm">
                      <Search className="h-4 w-4 shrink-0 text-[color:var(--muted)]" />
                      <input
                        readOnly
                        placeholder="Search for brands, products & baby essentials"
                        className="h-full w-full bg-transparent text-sm text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted)]"
                        aria-label="Search (demo)"
                      />
                    </div>
                  </div>
                </div>

                <div className="ml-auto flex items-center gap-2">
                  <div className="relative hidden lg:flex">
                    <button
                      type="button"
                      onClick={() => setPickupOpen((v) => !v)}
                      className="inline-flex items-center gap-2 rounded-full border border-[var(--line)] bg-[var(--card)] px-3 py-2.5 text-sm text-[color:var(--muted)]"
                    >
                      <MapPin className="h-5 w-5 text-[var(--accent)]" />
                      <span className="whitespace-nowrap">Pick Up in Store</span>
                      <ChevronDown className="h-4 w-4 text-[var(--accent)]" />
                    </button>
                    {pickupOpen ? (
                      <div className="absolute right-0 top-full z-[120] mt-2 w-[320px] rounded-2xl border border-[var(--line)] bg-[var(--card)] p-3 shadow-[0_18px_40px_rgba(15,23,42,0.12)]">
                        <div className="text-xs font-semibold text-[color:var(--ink)]">Alamat Pick Up</div>
                        <div className="mt-2 space-y-2 text-sm text-[color:var(--muted)]">
                          {pickupLocations.map((loc) => (
                            <div key={loc.name} className="rounded-xl border border-[var(--line)] px-3 py-2">
                              <div className="text-xs font-semibold text-[color:var(--ink)]">{loc.name}</div>
                              <div className="mt-1 text-xs">{loc.address}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--line)] bg-[var(--card)] sm:h-11 sm:w-11">
                    <Truck className="h-6 w-6 text-[var(--accent)]" />
                  </span>

                  <div className="hidden items-center gap-3 rounded-full border border-[var(--line)] bg-[var(--card)] px-4 py-2 lg:flex">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--line)]">
                      <User className="h-5 w-5 text-[var(--accent)]" />
                    </span>
                    <span className="flex flex-col leading-tight">
                      <span className="text-[11px] text-[color:var(--muted)]">My Account</span>
                      <span className="text-sm font-semibold text-[color:var(--ink)]">Sign In / Register</span>
                    </span>
                  </div>

                  <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--line)] bg-[var(--card)] sm:h-11 sm:w-11 lg:hidden">
                    <User className="h-6 w-6 text-[var(--accent)]" />
                  </span>

                  <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--line)] bg-[var(--card)] sm:h-11 sm:w-11">
                    <ShoppingBag className="h-6 w-6 text-[var(--accent)]" />
                    <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1 text-[11px] font-semibold text-white shadow-sm">
                      2
                    </span>
                  </span>
                </div>
              </div>

              <div className="pb-3 lg:hidden">
                <div className="flex h-11 w-full items-center gap-3 rounded-full border border-[var(--line)] bg-[var(--card)] px-4 text-left shadow-sm">
                  <Search className="h-4 w-4 text-[color:var(--muted)]" />
                  <span className="text-sm text-[color:var(--muted)]">Cari produk...</span>
                </div>
              </div>
            </div>

            <div className="border-t border-[var(--line)]">
              <div className="mx-auto hidden h-12 w-full max-w-[1280px] items-center gap-8 px-4 text-[15px] sm:px-6 lg:flex lg:px-8">
                <div className="group relative">
                  <span className="flex cursor-default items-center gap-2 font-semibold text-[color:var(--ink)]">
                    Shop by Brands <ChevronDown className="h-4 w-4 text-[var(--accent)]" />
                  </span>
                  <span className="absolute left-0 top-full h-3 w-full opacity-0" />
                  <div className="invisible absolute left-0 top-full z-50 w-[min(640px,calc(100vw-2rem))] rounded-3xl border border-[var(--line)] bg-[var(--card)] p-4 opacity-0 shadow-lg transition group-hover:visible group-hover:opacity-100">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-semibold tracking-wide text-[color:var(--muted)]">Popular Brands</div>
                      <span className="text-xs font-medium text-[color:var(--ink)]">Lihat semua &gt;</span>
                    </div>
                    <div className="mt-3 grid max-h-[240px] grid-cols-2 gap-2 overflow-auto pr-1">
                      {DEMO_BRANDS.map((b) => (
                        <button
                          key={b.id}
                          type="button"
                          className="rounded-2xl border border-[var(--line)] bg-[var(--card)] px-3 py-2 text-left text-xs text-[color:var(--ink)]"
                        >
                          {b.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="group relative">
                  <span className="flex cursor-default items-center gap-2 font-semibold text-[color:var(--ink)]">
                    Shop by Categories <ChevronDown className="h-4 w-4 text-[var(--accent)]" />
                  </span>
                  <span className="absolute left-0 top-full h-3 w-full opacity-0" />
                  <div className="invisible absolute left-0 top-full z-50 w-[min(640px,calc(100vw-2rem))] rounded-3xl border border-[var(--line)] bg-[var(--card)] p-4 opacity-0 shadow-lg transition group-hover:visible group-hover:opacity-100">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-semibold tracking-wide text-[color:var(--muted)]">Top Categories</div>
                      <span className="text-xs font-medium text-[color:var(--ink)]">Lihat semua &gt;</span>
                    </div>
                    <div className="mt-3 grid max-h-[240px] grid-cols-2 gap-2 overflow-auto pr-1">
                      {SHOP_CATEGORY_CARDS.map((c) => (
                        <button
                          key={c.label}
                          type="button"
                          onClick={() => selectCategory(c.filter)}
                          className="rounded-2xl border border-[var(--line)] bg-[var(--card)] px-3 py-2 text-left text-xs text-[color:var(--ink)]"
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <button type="button" className="font-semibold text-[color:var(--ink)]" onClick={() => selectCategory("all")}>
                  New Arrivals
                </button>
                <span className="font-semibold text-[color:var(--ink)]">Promotion</span>
                <span className="relative font-semibold text-[color:var(--ink)]">
                  <span className="relative z-10">Sale</span>
                  <svg
                    className="pointer-events-none absolute left-1/2 top-1/2 h-[72px] w-[72px] -translate-x-1/2 -translate-y-1/2"
                    viewBox="0 0 80 80"
                    aria-hidden
                  >
                    <path
                      d="M10 40c6-20 54-20 60 0c-4 22-54 24-60 2"
                      fill="none"
                      stroke="#ef4444"
                      strokeWidth="7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={styles.saleRingMain}
                    />
                  </svg>
                </span>
                <span className="font-semibold text-[color:var(--ink)]">About</span>
              </div>
            </div>
          </header>

          {mobileOpen ? (
            <div className="fixed inset-0 z-[200] lg:hidden">
              <button
                type="button"
                className="absolute inset-0 bg-black/50"
                aria-label="Close menu"
                onClick={() => setMobileOpen(false)}
              />
              <div className="absolute left-0 top-0 flex h-full w-[min(20rem,90vw)] flex-col border-r border-[var(--line)] bg-[var(--card)] p-4 shadow-xl">
                <div className="mb-4 flex items-center justify-between">
                  <span className="font-semibold text-[color:var(--ink)]">Menu</span>
                  <button type="button" onClick={() => setMobileOpen(false)} className="rounded-full p-2 hover:bg-slate-100">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <nav className="flex flex-col gap-2 text-sm font-semibold">
                  <button type="button" className="rounded-xl px-3 py-2 text-left hover:bg-slate-50" onClick={() => selectCategory("all")}>
                    Semua produk
                  </button>
                  {SHOP_CATEGORY_CARDS.map((c) => (
                    <button
                      key={c.label}
                      type="button"
                      className="rounded-xl px-3 py-2 text-left hover:bg-slate-50"
                      onClick={() => selectCategory(c.filter)}
                    >
                      {c.label}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          ) : null}

          <div className="w-full px-3 sm:px-4">
            <div className="mx-auto w-full max-w-[1680px] pb-5 pt-3 md:pb-6 md:pt-4">
              <div className="grid gap-4 lg:grid-cols-[1.55fr_0.8fr]">
                <HeroCarousel slides={SHOP_HERO_SLIDES} />

                <div className="rounded-[32px] border border-[var(--line)] bg-white/90 p-5 shadow-[var(--shadow-soft)] md:p-6">
                  <div className="inline-flex rounded-full border border-[var(--line)] bg-[#fff2e2] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[color:var(--secondary)]">
                    Curated for Parents
                  </div>
                  <h1 className="mt-3 font-['Fraunces',Georgia,serif] text-2xl font-semibold leading-tight text-[color:var(--ink)] md:text-[2rem]">
                    Belanja kebutuhan si kecil dengan pengalaman yang lebih nyaman.
                  </h1>
                  <p className="mt-2 text-sm leading-relaxed text-[color:var(--muted)]">
                    Katalog terkurasi, proses checkout simpel, dan update promo harian untuk kebutuhan bayi serta ibu.
                  </p>
                  <div className="mt-5 grid gap-2 text-sm">
                    {["Produk aman dan terpilih", "Navigasi cepat untuk belanja harian", "Voucher dan promo yang jelas"].map((t) => (
                      <div key={t} className="flex items-center gap-2 rounded-2xl border border-[var(--line)] bg-white px-3 py-2">
                        <span className="h-2 w-2 shrink-0 rounded-full bg-[var(--accent)]" />
                        <span className="text-[color:var(--ink)]/90">{t}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={scrollToKatalog}
                      className="inline-flex items-center justify-center rounded-full bg-[var(--accent)] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[var(--accent-hover)]"
                    >
                      Mulai Belanja
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-full border border-[var(--line)] bg-white px-4 py-2.5 text-sm font-semibold text-[color:var(--ink)] transition hover:border-[var(--line-strong)]"
                    >
                      Lacak Pesanan
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mx-auto w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
            <section>
              <div className="grid gap-3 rounded-[30px] border border-[var(--line)] bg-white/90 p-4 shadow-[var(--shadow-soft)] sm:grid-cols-2 lg:grid-cols-4">
                {SHOP_TRUST_HIGHLIGHTS.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-2xl border border-[var(--line)] bg-gradient-to-b from-white to-[#fff8ef] p-3"
                  >
                    <div className="text-sm font-semibold text-[color:var(--ink)]">{item.title}</div>
                    <div className="mt-1 text-xs text-[color:var(--muted)]">{item.desc}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-6">
              <div className="rounded-[30px] border border-[var(--line)] bg-white/90 p-4 shadow-[var(--shadow-soft)] md:p-5">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[color:var(--muted)]">Kategori Pilihan</div>
                    <h2 className="mt-1 font-['Fraunces',Georgia,serif] text-2xl font-semibold text-[color:var(--ink)]">
                      Cari Produk Berdasarkan Kebutuhan
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => selectCategory("all")}
                    className="text-sm font-semibold text-[color:var(--secondary)] hover:underline"
                  >
                    Lihat semua produk
                  </button>
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {SHOP_CATEGORY_CARDS.map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => selectCategory(item.filter)}
                      className={cn(
                        "rounded-3xl border border-[var(--line)] bg-gradient-to-br p-4 text-left transition hover:-translate-y-0.5",
                        item.tone,
                        cat === item.filter && "ring-2 ring-[var(--secondary)] ring-offset-2"
                      )}
                    >
                      <div className="text-sm font-semibold text-[color:var(--ink)]">{item.label}</div>
                      <div className="mt-1 text-xs text-[color:var(--muted)]">{item.hint}</div>
                    </button>
                  ))}
                </div>
              </div>
            </section>

            <section className="mt-8">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[color:var(--muted)]">Voucher Spesial</div>
                  <h2 className="mt-1 font-['Fraunces',Georgia,serif] text-2xl font-semibold text-[color:var(--ink)]">Promo Hari Ini</h2>
                </div>
                <span className="text-sm font-semibold text-[color:var(--secondary)]">Lihat promo</span>
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-3">
                {SHOP_VOUCHERS.map((v) => (
                  <div
                    key={v.id}
                    className="relative overflow-hidden rounded-[30px] border border-[var(--line)] bg-gradient-to-br from-[#2f5d8a] via-[#3f7aae] to-[#4b8bc5] p-5 text-white shadow-[var(--shadow-soft)]"
                  >
                    <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-white/15" />
                    <div className="relative">
                      <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/80">Kode Voucher</div>
                      <div className="mt-1 text-xl font-semibold">{v.code}</div>
                      <div className="mt-3 text-5xl font-semibold leading-none">{formatVoucherValue(v)}</div>
                      <div className="mt-2 text-xs text-white/80">Min. pembelian {formatIdr(v.minSubtotal)}</div>
                      <span className="mt-4 inline-flex rounded-full bg-white px-4 py-1.5 text-xs font-semibold text-[#2f5d8a]">
                        Pakai Voucher
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-10">
              <div className="flex items-baseline justify-between">
                <h2 className="font-['Fraunces',Georgia,serif] text-2xl font-semibold text-[color:var(--ink)]">Produk Terlaris</h2>
                <button
                  type="button"
                  onClick={() => selectCategory("all")}
                  className="text-sm font-semibold text-[color:var(--secondary)] hover:underline"
                >
                  Lihat semua
                </button>
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-3">
                {best.map((p) => {
                  const hasSale = saleActive(p);
                  const price = effectivePrice(p);
                  return (
                    <div
                      key={`best-${p.id}`}
                      className="group rounded-[28px] border border-[var(--line)] bg-white/95 p-4 shadow-[var(--shadow-soft)] transition hover:-translate-y-0.5"
                    >
                      <div className="block">
                        <div className="relative h-48 overflow-hidden rounded-2xl">
                          <img
                            alt=""
                            src={p.image}
                            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.05]"
                          />
                        </div>
                        <div className="mt-3">
                          <div className="line-clamp-2 text-sm font-semibold text-[color:var(--ink)]">{p.name}</div>
                          <div className="mt-1 flex items-center gap-2 text-xs text-[color:var(--muted)]">
                            <div className="flex items-center gap-0.5">{renderStars(p.ratingAvg)}</div>
                            <span>{p.ratingAvg.toFixed(1)}</span>
                            <span>({p.ratingCount})</span>
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <div className="text-sm font-semibold text-[color:var(--ink)]">{formatIdr(price)}</div>
                            {hasSale ? (
                              <div className="text-xs text-[color:var(--muted)] line-through">{formatIdr(p.price)}</div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <button
                          type="button"
                          disabled={p.stock === 0}
                          className={cn(
                            "w-full rounded-full px-4 py-2 text-xs font-semibold transition duration-200",
                            p.stock === 0
                              ? "cursor-not-allowed border border-[var(--line)] bg-white text-[color:var(--muted)]"
                              : "bg-[var(--accent)] text-white shadow-[0_14px_32px_var(--glow)] hover:-translate-y-0.5 hover:bg-[var(--accent-hover)]"
                          )}
                        >
                          {p.stock === 0 ? "Habis" : "Tambah ke Keranjang"}
                        </button>
                        {hasSale ? (
                          <span className="shrink-0 rounded-full border border-[var(--line)] bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--muted)]">
                            Sale
                          </span>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section ref={katalogRef} id="katalog" className="mt-10 scroll-mt-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="font-['Fraunces',Georgia,serif] text-2xl font-semibold text-[color:var(--ink)]">Produk Baru</h2>
                <div className="flex flex-wrap items-center gap-2">
                  {activeLabel ? (
                    <span className="text-sm text-[color:var(--muted)]">
                      Filter: <strong className="text-[color:var(--ink)]">{activeLabel}</strong>
                      <button
                        type="button"
                        onClick={() => setCat("all")}
                        className="ml-2 text-sm font-semibold text-[color:var(--secondary)] hover:underline"
                      >
                        Hapus
                      </button>
                    </span>
                  ) : (
                    <span className="text-sm font-semibold text-[color:var(--secondary)]">Jelajahi katalog</span>
                  )}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
                {filtered.length ? (
                  filtered.map((p) => {
                    const hasSale = saleActive(p);
                    const price = effectivePrice(p);
                    const discount = hasSale ? Math.round(((p.price - price) / p.price) * 100) : 0;
                    return (
                      <div
                        key={p.id}
                        className="group rounded-[28px] border border-[var(--line)] bg-white/95 p-3 shadow-[var(--shadow-soft)] transition hover:-translate-y-0.5"
                      >
                        <div className="relative aspect-square overflow-hidden rounded-2xl">
                          <img
                            alt=""
                            src={p.image}
                            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.06]"
                          />
                          {hasSale ? (
                            <div className="absolute left-2 top-2 rounded-full bg-[var(--accent)] px-2 py-1 text-[10px] font-semibold text-white">
                              -{discount}%
                            </div>
                          ) : null}
                        </div>
                        <div className="mt-3">
                          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--muted)]">{p.brand}</div>
                          <div className="line-clamp-2 text-sm font-medium text-[color:var(--ink)]">{p.name}</div>
                          <div className="mt-1 flex items-center gap-2 text-xs text-[color:var(--muted)]">
                            <div className="flex items-center gap-0.5">{renderStars(p.ratingAvg)}</div>
                            <span>{p.ratingAvg.toFixed(1)}</span>
                            <span>({p.ratingCount})</span>
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <div className="text-sm font-semibold text-[color:var(--ink)]">{formatIdr(price)}</div>
                            {hasSale ? (
                              <div className="text-xs text-[color:var(--muted)] line-through">{formatIdr(p.price)}</div>
                            ) : null}
                          </div>
                          {p.stock === 0 ? (
                            <div className="mt-2">
                              <span className="rounded-full border border-[var(--line)] bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--muted)]">
                                Stok habis
                              </span>
                            </div>
                          ) : null}
                        </div>
                        <div className="mt-3 flex items-center justify-between gap-2">
                          <button
                            type="button"
                            disabled={p.stock === 0}
                            className={cn(
                              "rounded-full px-4 py-2 text-xs font-semibold transition duration-200",
                              p.stock === 0
                                ? "cursor-not-allowed border border-[var(--line)] bg-white text-[color:var(--muted)]"
                                : "bg-[var(--accent)] text-white shadow-[0_14px_32px_var(--glow)] hover:-translate-y-0.5 hover:bg-[var(--accent-hover)]"
                            )}
                          >
                            {p.stock === 0 ? "Habis" : "Tambah ke Keranjang"}
                          </button>
                          <span className="text-xs font-semibold text-[color:var(--secondary)]">Detail</span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="col-span-full rounded-[28px] border border-[var(--line)] bg-white p-6 text-sm text-[color:var(--muted)]">
                    Tidak ada produk yang cocok dengan filter saat ini.
                  </div>
                )}
              </div>
            </section>

            <section className="mt-8">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {SHOP_QUICK_LINKS.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-[22px] border border-[var(--line)] bg-white/90 p-4 shadow-[var(--shadow-soft)] transition hover:-translate-y-0.5"
                  >
                    <div className="text-sm font-semibold text-[color:var(--ink)]">{item.title}</div>
                    <div className="mt-1 text-xs text-[color:var(--muted)]">{item.desc}</div>
                  </div>
                ))}
              </div>
            </section>

            <p className="mt-6 text-center text-xs text-[color:var(--muted)]">
              Pratinjau statis — perilaku penuh (cart, checkout, admin) di app{" "}
              <code className="rounded bg-white/80 px-1">Baby-shop</code> via <strong>Embed</strong> / <strong>Tab baru</strong>.
            </p>

            <StoreFooter />
          </div>

          <MobileBottomNavDemo />
        </div>
      ) : (
        <div className="relative flex min-h-0 flex-1 flex-col bg-slate-900">
          <div className="border-b border-slate-800 bg-slate-900/95 px-4 py-2 text-center text-xs text-amber-100/90">
            Memuat app dari <span className="font-mono text-amber-200">{liveBase}</span>. Jalankan{" "}
            <code className="rounded bg-slate-800 px-1">npm run dev:demo</code> di folder Baby-shop jika kosong.
          </div>
          <iframe title="LittleNest Baby Shop — Next.js" src={`${liveBase}/`} className="h-full min-h-[50vh] w-full flex-1 border-0 bg-white" />
        </div>
      )}
    </div>
  );
}
